// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import {
  TrashStatus,
  TrashTimeRowsRequest,
  TrashTimeRowsResponse
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { timelineClient } from "app/rpc/client"
import { grpc } from "../../../app/rpc/executor"
import { isGrpcError } from "../../../lib/rpcutils"
import { prepareListDaySummaryRequest } from "./daySummaries"
import { RootState } from "../../../app/store"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type TrashTimeRowsRpc = (request: TrashTimeRowsRequest) => Observable<TrashTimeRowsResponse>

export const trashTimeRows = (trashTimeRowsRpc: TrashTimeRowsRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.trashTimeRows.match),
      withLatestFrom(state$),
      mergeMap(([action, state]) => {
        const timeRowIdsList = action.payload.getTimeRowIdsList()
        return concat(
          of(
            // mark rows as being trashed
            TimelineSlice.setUpdatingTimeRowsStatus({
              trashStatus: TrashStatus.TRASHED,
              timeRowIds: timeRowIdsList,
            })
          ),
          of(action.payload)
            .pipe(
              mergeMap(trashTimeRowsRpc),
              mergeMap(_ => concat(
                // set trashed status for rows
                of(TimelineSlice.updateTimeRowsStatus({
                  trashStatus: TrashStatus.TRASHED,
                  timeRowIds: timeRowIdsList,
                })),
                // remove trashed rows from action pane
                of(TimelineSlice.removeActiveTimeRowIds({ timeRowIds: timeRowIdsList })),
                // remove pending trashing
                of(TimelineSlice.removeUpdatingTimeRowsStatus(timeRowIdsList)),
                of(TimelineAdditionalUiSlice.setBlinkTrashIcon(true)),
                of(Actions.listDaySummaries(prepareListDaySummaryRequest(state.timeline.activeDate.toString()))
                )
              )),
              catchError(e => concat(
                // unmark rows as being trashed
                of(TimelineSlice.removeUpdatingTimeRowsStatus(timeRowIdsList)),
                showErrorNotification(e)
              )),
            ),
        )
      }),
    )

const showErrorNotification = (e: unknown): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return of(showSnackBar({
        message: t({ id: "msg_trash_time_rows_failed_not_found", message: "Failed to move time rows to trash. Could not find the target time row or time group." }),
        messageType: "ERROR",
      }))
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_trash_time_rows_failed_permission_denied", message: "Failed to move time rows to trash. You do not have permission to trash the time row or time group." }),
        messageType: "ERROR",
      }))
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_trash_time_rows_failed_precondition", message: "Failed to trash time rows. Time rows may be out of sync." }),
          messageType: "ERROR",
        })),
        of(Actions.onTimelineOutOfSync()),
      )
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_trash_time_rows_failed", message: "Failed to move time rows to trash. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const trashTimeRowsRpc = (request: TrashTimeRowsRequest): Observable<TrashTimeRowsResponse> =>
  grpc(() => timelineClient.trashTimeRows(request, null))

export default trashTimeRows(trashTimeRowsRpc)
